import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ReactComponent as SearchIcon } from "images/profile/search-normal.svg";
import { ReactComponent as CalendarIcon } from "images/profile/calendar.svg";
import { ReactComponent as CloseIcon } from "images/profile/Close.svg";
import SelectComponent from "components/selectWithComponents";
import DatePickerComponent from "components/datePicker";
import moment from "moment";

function ProjectFilters({
  searchTerm,
  setSearchTerm,
  selectedServices,
  setSelectedServices,
  selectedDeadline,
  setSelectedDeadline,
  serviceOptions,
  setFilterQuery,
  disabled = false,
  isVendor = false,
}) {
  const [t] = useTranslation();

  const handleClear = () => {
    setSearchTerm("");
    setSelectedServices([]);
    setSelectedDeadline(null);
    setFilterQuery((prev) => ({
      filter: { op: "and", value: [] },
      paginate: { ...prev.paginate, pageNumber: 1 },
    }));
  };

  return (
    <div className="flex flex-wrap gap-[16px] self-stretch">
      {/* Search Input */}
      <div className="relative w-[221px] z-50">
        <input
          type="text"
          placeholder={
            isVendor ? t("searchTaskPlaceholderKey") : t("searchPlaceholderKey")
          }
          autoCorrect="off"
          autoComplete="off"
          spellCheck="false"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={disabled}
          className={`flex h-[44px] w-full items-center py-[8px] px-[14px]
            self-stretch rounded-[6px] border border-[#E4E4E7] ${
              disabled ? "bg-[#F4F4F5] cursor-not-allowed" : "bg-[#fff]"
            }
            outline-none text-[#1D1B22] font-regular
             text-[16px] not-italic placeholder:font-regular placeholder:text-[16px]
             placeholder:not-italic placeholder:font-normal pr-[40px]`}
        />

        <SearchIcon className="absolute top-[10px] right-[14px]" />
      </div>

      {/* Service Filter */}
      <div className="min-w-[200px]">
        <SelectComponent
          placeholder={isVendor ? t("skill") : t("serviceLabel")}
          options={serviceOptions}
          value={selectedServices}
          isDisabled={disabled}
          onChange={(values) => {
            setSelectedServices(values);
            let vendorSearchKey = isVendor
              ? "$project->subGroup->productGroup.id$"
              : "$subGroup->productGroup.id$";
            setFilterQuery((prevQuery) => {
              const findOrderNumber = prevQuery.filter.value.find(
                (item) => item.key === vendorSearchKey
              );

              let value = prevQuery.filter.value;
              if (findOrderNumber) {
                value = prevQuery.filter.value.map((item) => {
                  if (item.key === vendorSearchKey) {
                    item.value = values.map((service) => service.value);
                  }
                  return item;
                });
              } else {
                value.push({
                  op: "in",
                  key: vendorSearchKey,
                  value: values.map((service) => service.value),
                });
              }

              if (!values.length) {
                value = value.filter((item) => item.key !== vendorSearchKey);
              }

              return {
                filter: {
                  ...prevQuery.filter,
                  value: value,
                },
                paginate: {
                  ...prevQuery.paginate,
                  pageNumber: 1,
                },
              };
            });
          }}
          isMulti={true}
          withCheckBox={true}
          closeMenuOnSelect={false}
          containerClassName="w-full"
          customInputStyle={`flex h-[44px] w-full items-center py-[8px] px-[14px] self-stretch rounded-[6px] border border-[#E4E4E7] ${
            disabled ? "bg-[#F4F4F5] cursor-not-allowed" : "bg-[#fff]"
          } outline-none text-[#1D1B22] font-regular text-[16px] not-italic`}
        />
      </div>

      {/* Deadline Filter */}
      <div className="min-w-[200px] relative">
        <DatePickerComponent
          placeholder={t("deadlineLabel")}
          value={selectedDeadline}
          disabled={disabled}
          onValueChange={(date) => {
            setSelectedDeadline(date);
            setFilterQuery((prevQuery) => {
              let vendorSearchKey = isVendor
                ? "$project.delivery_date$"
                : "delivery_date";
              const findOrderNumber = prevQuery.filter.value.find(
                (item) => item.key === vendorSearchKey
              );

              let value = prevQuery.filter.value;
              if (findOrderNumber) {
                value = prevQuery.filter.value.map((item) => {
                  if (item.key === vendorSearchKey) {
                    item.value = [
                      moment(+new Date(date))
                        .startOf("day")
                        .format("YYYY-MM-DD HH:mm:ss"),
                      moment(+new Date(date))
                        .endOf("day")
                        .format("YYYY-MM-DD HH:mm:ss"),
                    ];
                  }
                  return item;
                });
              } else {
                value.push({
                  op: "bwn",
                  key: vendorSearchKey,
                  value: [
                    moment(+new Date(date))
                      .startOf("day")
                      .format("YYYY-MM-DD HH:mm:ss"),
                    moment(+new Date(date))
                      .endOf("day")
                      .format("YYYY-MM-DD HH:mm:ss"),
                  ],
                });
              }

              if (!date) {
                value = value.filter((item) => item.key !== vendorSearchKey);
              }

              return {
                filter: {
                  ...prevQuery.filter,
                  value: value,
                },
                paginate: {
                  ...prevQuery.paginate,
                  pageNumber: 1,
                },
              };
            });
          }}
          dateFormat="dd/MM/yyyy"
          containerClassName="w-full"
          customInputStyle={`flex h-[44px] w-full items-center py-[8px] px-[14px] self-stretch rounded-[6px] border border-[#E4E4E7] ${
            disabled ? "bg-[#F4F4F5] cursor-not-allowed" : "bg-[#fff]"
          } outline-none text-[#1D1B22] font-regular text-[16px] not-italic placeholder:text-[rgba(29,27,34,0.40)] pr-[40px]`}
        />
        <CalendarIcon
          className="absolute top-[10px] right-[14px] pointer-events-none"
          style={{ opacity: disabled ? 0.5 : 1 }}
        />
      </div>

      {/* Clear Button */}
      <button
        type="button"
        onClick={handleClear}
        disabled={disabled}
        className={`h-[44px] px-[14px] rounded-[6px] border border-[#E4E4E7] bg-white text-[#1D1B22] ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-[#F3F5F7]"
        }`}
      >
        {t("clearFilters")}
      </button>
    </div>
  );
}

ProjectFilters.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  selectedStatuses: PropTypes.array.isRequired,
  setSelectedStatuses: PropTypes.func.isRequired,
  selectedServices: PropTypes.array.isRequired,
  setSelectedServices: PropTypes.func.isRequired,
  selectedDeadline: PropTypes.object,
  setSelectedDeadline: PropTypes.func.isRequired,
  serviceOptions: PropTypes.array.isRequired,
  setFilterQuery: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default ProjectFilters;
